import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
 import Button from'../ui/Button';
 import Input from'../ui/Input';
 import Icon from'../AppIcon';

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    username: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  const { signUp, authError, clearError } = useAuth()
  const navigate = useNavigate()

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    clearError()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (formData.password !== formData.confirmPassword) {
      return
    }
    
    setIsLoading(true)
    
    try {
      const result = await signUp(formData.email, formData.password, {
        full_name: formData.fullName,
        username: formData.username
      })
      
      if (result?.success) {
        navigate('/birth-data-setup')
      }
    } catch (error) {
      console.log('Signup error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const passwordMismatch = formData.password !== formData.confirmPassword && formData.confirmPassword !== ''

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6 animate-cosmic-glow">
            <Icon name="Stars" size={32} color="white" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-text-primary">
            Join AstroTone
          </h2>
          <p className="mt-2 text-sm text-text-secondary">
            Create your cosmic musical profile
          </p>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
                Full Name
              </label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                required
                placeholder="Enter your full name"
                value={formData.fullName}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-text-primary mb-2">
                Username
              </label>
              <Input
                id="username"
                name="username"
                type="text"
                required
                placeholder="Choose a username"
                value={formData.username}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Email Address
              </label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pr-10"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <Icon 
                    name={showPassword ? 'EyeOff' : 'Eye'} 
                    size={18} 
                    color="rgb(var(--color-text-secondary))" 
                  />
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full pr-10 ${passwordMismatch ? 'border-red-500' : ''}`}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  <Icon 
                    name={showConfirmPassword ? 'EyeOff' : 'Eye'} 
                    size={18} 
                    color="rgb(var(--color-text-secondary))" 
                  />
                </button>
              </div>
              {passwordMismatch && (
                <p className="mt-1 text-sm text-red-400">Passwords do not match</p>
              )}
            </div>
          </div>

          {/* Error Message */}
          {authError && (
            <div className="glass bg-red-500/10 border border-red-500/20 rounded-xl p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={18} color="rgb(239 68 68)" />
                <p className="text-sm text-red-400">{authError}</p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading || passwordMismatch}
            className="w-full"
            size="lg"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Creating account...</span>
              </div>
            ) : (
              'Create Account'
            )}
          </Button>

          <div className="text-center">
            <span className="text-sm text-text-secondary">
              Already have an account?{' '}
              <Link 
                to="/login" 
                className="font-medium text-accent hover:text-accent/80 transition-colors"
              >
                Sign in
              </Link>
            </span>
          </div>
        </form>

        {/* Terms */}
        <div className="text-center">
          <p className="text-xs text-text-secondary">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-accent hover:text-accent/80">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-accent hover:text-accent/80">
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default Signup